import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Save, Loader2, Plus, Trash2, LayoutGrid, Bot } from 'lucide-react';
import QueSeConfiguraAca from '@/components/QueSeConfiguraAca';

function slugify(value) {
  return (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

const DEFAULT_PM = {
  enabled: false, channel: 'whatsapp', standup_hour: 9, followup_hour: 17,
  utc_offset_hours: -3, remind_due: true, remind_effort: true, remind_tests: true,
};

export default function CapsuleWorkflowTab({ config, handleSave, saving, capsuleIntents = [], teamRoles = [] }) {
  const [templates, setTemplates] = useState([]);
  const [pm, setPm] = useState(DEFAULT_PM);

  useEffect(() => {
    setPm({ ...DEFAULT_PM, ...(config?.pm_policy || {}) });
    setTemplates(
      // **Se conserva la plantilla entera**, igual que las subtareas: reconstruirla con las
      // claves conocidas le borraba `reparto_del_agente` —y a lo que venga despues— en el
      // primer guardado. Es la misma falla del 2026-08-12, un nivel mas arriba.
      (config?.workflow_templates || []).map((t) => ({
        ...t,
        template_id: t.template_id || '',
        name: t.name || '',
        ticket_type: t.ticket_type || 'support',
        intents: t.intents || [],
        phases: (t.phases || []).map((p) => ({
          key: p.key || '',
          label: p.label || '',
          requires_verification: !!p.requires_verification,
          rol: p.rol || '',
          // Objetos, no texto. Se unian con `\n` y volvian como strings, asi que el primer
          // guardado desde esta pantalla habria borrado **todos** los roles de subtarea. Es
          // la falla del 2026-08-12 un nivel mas abajo: la UI reconstruye lo que conoce y se
          // lleva puesto lo que no.
          // **Y se conserva lo que esta pantalla no muestra.** El comentario de arriba
          // nombra la falla del 2026-08-12 y el codigo la repetia un nivel mas abajo: al
          // reconstruir la subtarea con dos campos, `peso_pct` —el reparto de esfuerzo— se
          // perdia en cada guardado, en silencio y sin que nadie lo hubiera pedido.
          default_subtasks: (p.default_subtasks || []).map((s) => (
            typeof s === 'string'
              ? { title: s, rol: '' }
              : { ...s, title: s.title || '', rol: s.rol || '', peso_pct: s.peso_pct ?? null }
          )),
        })),
      }))
    );
  }, [config]);

  const intentOptions = (capsuleIntents || []).filter((i) => i?.id).map((i) => ({
    id: slugify(i.id),
    label: i.name || i.id,
  }));

  const assignedIntentMap = {};
  templates.forEach((t) => {
    (t.intents || []).forEach((intentId) => {
      assignedIntentMap[intentId] = t.name || t.template_id;
    });
  });
  const orphanIntents = intentOptions.filter((i) => !assignedIntentMap[i.id]);

  const addTemplate = () =>
    setTemplates((t) => [
      ...t,
      { template_id: '', name: 'Nueva plantilla', ticket_type: 'support', intents: [], phases: [] },
    ]);
  const removeTemplate = (idx) => setTemplates((t) => t.filter((_, i) => i !== idx));
  const updateTemplate = (idx, patch) =>
    setTemplates((t) => t.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const toggleTemplateIntent = (tIdx, intentId) => {
    setTemplates((t) =>
      t.map((it, i) => {
        if (i !== tIdx) return it;
        const has = (it.intents || []).includes(intentId);
        return {
          ...it,
          intents: has ? it.intents.filter((x) => x !== intentId) : [...(it.intents || []), intentId],
        };
      })
    );
  };
  const addPhase = (tIdx) =>
    setTemplates((t) =>
      t.map((it, i) =>
        i === tIdx
          ? { ...it, phases: [...it.phases, { key: '', label: '', requires_verification: false, rol: '', default_subtasks: [] }] }
          : it
      )
    );
  const updatePhase = (tIdx, pIdx, patch) =>
    setTemplates((t) =>
      t.map((it, i) =>
        i === tIdx ? { ...it, phases: it.phases.map((p, j) => (j === pIdx ? { ...p, ...patch } : p)) } : it
      )
    );
  const conSubtareas = (tIdx, pIdx, fn) =>
    setTemplates((t) =>
      t.map((it, i) => (i !== tIdx ? it : {
        ...it,
        phases: it.phases.map((p, j) => (j !== pIdx ? p : { ...p, default_subtasks: fn(p.default_subtasks || []) })),
      }))
    );
  const addSubtask = (tIdx, pIdx) => conSubtareas(tIdx, pIdx, (s) => [...s, { title: '', rol: '' }]);
  const removeSubtask = (tIdx, pIdx, sIdx) => conSubtareas(tIdx, pIdx, (s) => s.filter((_, k) => k !== sIdx));
  const updateSubtask = (tIdx, pIdx, sIdx, patch) =>
    conSubtareas(tIdx, pIdx, (s) => s.map((it, k) => (k === sIdx ? { ...it, ...patch } : it)));

  const removePhase = (tIdx, pIdx) =>
    setTemplates((t) =>
      t.map((it, i) => (i === tIdx ? { ...it, phases: it.phases.filter((_, j) => j !== pIdx) } : it))
    );

  const savePm = () =>
    handleSave('pm_policy', {
      enabled: !!pm.enabled,
      channel: pm.channel || 'whatsapp',
      standup_hour: parseInt(pm.standup_hour, 10) || 9,
      followup_hour: parseInt(pm.followup_hour, 10) || 17,
      utc_offset_hours: parseInt(pm.utc_offset_hours, 10) || 0,
      remind_due: !!pm.remind_due,
      remind_effort: !!pm.remind_effort,
      remind_tests: !!pm.remind_tests,
    });

  const saveTemplates = () => {
    const payload = templates.map((t) => ({
      ...t,
      template_id: slugify(t.template_id || t.name || t.ticket_type),
      name: (t.name || '').trim() || t.ticket_type,
      ticket_type: t.ticket_type || 'support',
      intents: (t.intents || []).map(slugify).filter(Boolean),
      // `roles` no se manda: el backend lo recalcula como la union de los roles que las fases
      // y subtareas usan de verdad. Era una lista escrita a mano que podia mentir.
      phases: (t.phases || [])
        .filter((p) => (p.label || p.key || '').trim())
        .map((p) => ({
          key: slugify(p.key || p.label),
          label: (p.label || '').trim() || slugify(p.key),
          requires_verification: !!p.requires_verification,
          rol: p.rol || null,
          default_subtasks: (p.default_subtasks || [])
            .filter((s) => (s.title || '').trim())
            // Se manda la subtarea entera: lo que la pantalla no conoce viaja igual. Es el
            // mismo criterio que ya esta escrito en `normalize_workflow_template` —preferimos
            // arrastrar una clave que nadie lee antes que borrar una que alguien configuro—.
            .map((s) => ({
              ...s, title: s.title.trim(), rol: s.rol || null,
              peso_pct: s.peso_pct === '' || s.peso_pct == null ? null : Number(s.peso_pct),
            })),
        })),
    }));
    handleSave('workflow_templates', payload);
  };

  return (
    <div className="space-y-4">
      {/* **Acá se parametriza casi todo lo que un caso hace solo**, y hasta hoy era de las pocas
          pantallas de Config Cápsula sin explicación. La solapa la tenía; el editor de adentro,
          que es donde se decide quién recibe cada tarea y qué autorizaciones nacen con el caso,
          no decía nada. */}
      <QueSeConfiguraAca
        testId="ayuda-plantillas-de-trabajo"
        queEs="Los pasos que se crean solos cuando entra un caso de cierto tipo: qué hay que hacer, en qué orden, y quién lo recibe."
        paraQue="Los pedidos que se repiten tienen siempre los mismos pasos. Declararlos una vez evita armarlos a mano cada vez y evita que se olvide uno — que es lo que pasa cuando el que sabe está de licencia."
        aQueAfecta={<>A los <b>casos nuevos</b> del tipo que la plantilla declara: al clasificarse, se les crean las tareas. <b>No toca los casos que ya existen</b>, así que cambiar una plantilla no arregla lo que ya salió mal.</>}
        ejemplo={<>Un pedido de desarrollo genera análisis, desarrollo, prueba y pase. Cada paso declara <b>un rol y no una persona</b>, así la plantilla sigue sirviendo cuando cambia el equipo. Y un paso puede ser una <b>autorización</b>: nace asignada a quien corresponda —el jefe de quien pidió, el gerente de su área, un rol— sin que nadie tenga que acordarse de pedirla.</>}
        ojo={<>Si prendés <b>«el agente reparte»</b>, el agente decide a quién le da cada tarea que cree en un caso de esta plantilla, en vez de dárselas a quien tiene el caso. Es lo que querés en una plantilla de uso automático y lo que <b>no</b> querés en una donde el reparto ya está pensado. Y una autorización <b>no frena el caso</b>: se registra y se avisa, y qué hacer con un rechazo lo decide quien tiene el caso.</>}
      />

      <Card className="rounded-sm border-zinc-200">
        <CardHeader className="pb-2 border-b border-zinc-100">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2">
            <LayoutGrid className="w-4 h-4" /> Plantillas de trabajo
          </CardTitle>
          <p className="text-[11px] text-zinc-600 font-normal normal-case pt-1">
            Asocia cada plantilla a uno o mas intents. Al crear un ticket (manual o por agente), se aplica segun el intent clasificado.
            Configura intents en el tab Intents.
          </p>
          {orphanIntents.length > 0 && (
            <p className="text-[11px] text-amber-700 font-normal normal-case pt-1">
              Intents sin plantilla: {orphanIntents.map((i) => i.label).join(', ')}
            </p>
          )}
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          {templates.map((t, tIdx) => (
            <div key={tIdx} className="border border-zinc-200 rounded-sm p-3 space-y-3 bg-zinc-50/40">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-zinc-500">Nombre</Label>
                  <Input value={t.name} onChange={(e) => updateTemplate(tIdx, { name: e.target.value })} className="h-8 text-xs rounded-sm mt-0.5" />
                </div>
                <div>
                  <Label className="text-[10px] text-zinc-500">Roles (coma)</Label>
                  <Input value={t.roles} onChange={(e) => updateTemplate(tIdx, { roles: e.target.value })} placeholder="analista, desarrollador" className="h-8 text-xs rounded-sm mt-0.5" />
                </div>
              </div>

              {/* **Quien reparte las tareas que el agente crea.** Es una decision de la
                  plantilla y no del agente: en el caso 20260901-2 la plantilla ya habia
                  asignado por rol y el agente creo una tarea mas para otra persona, elegida por
                  su cargo. Elegir por el titulo es lo que un modelo hace bien cuando no hay una
                  regla — asi que la regla se declara. */}
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  data-testid={`reparto-del-agente-${tIdx}`}
                  checked={t.reparto_del_agente === true}
                  onChange={(e) => updateTemplate(tIdx, { reparto_del_agente: e.target.checked })}
                />
                <span>
                  <span className="text-[11px] text-zinc-700">
                    El agente puede repartir las tareas de esta plantilla
                  </span>
                  <span className="block text-[10px] text-zinc-500">
                    Apagado, el agente <b>igual crea tareas</b>, pero van a quien tenga el caso o
                    el rol declarado en la fase. Prendido, elige la persona a criterio: sirve
                    para un trabajo que no se sabe de antemano en cuántas partes se divide.
                  </span>
                </span>
              </label>

              <div>
                <Label className="text-[10px] text-zinc-500 uppercase tracking-wider">Intents asociados</Label>
                {!intentOptions.length ? (
                  <p className="text-[11px] text-zinc-500 mt-1">Define intents en el tab Intents para poder asociarlos.</p>
                ) : (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {intentOptions.map((opt) => {
                      const selected = (t.intents || []).includes(opt.id);
                      const usedElsewhere = assignedIntentMap[opt.id] && assignedIntentMap[opt.id] !== (t.name || t.template_id);
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => toggleTemplateIntent(tIdx, opt.id)}
                          className={`text-[10px] px-2 py-1 rounded-sm border ${selected ? 'bg-brand-primary text-white border-brand-primary' : 'bg-white border-zinc-200 text-zinc-700'}`}
                          title={usedElsewhere ? `Tambien usado en ${assignedIntentMap[opt.id]}` : opt.label}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] text-zinc-500 uppercase tracking-wider">Fases</Label>
                {t.phases.map((p, pIdx) => (
                  <div key={pIdx} className="border border-zinc-200 rounded-sm p-2 bg-white space-y-2">
                    <div className="flex items-center gap-2">
                      <Input value={p.label} onChange={(e) => updatePhase(tIdx, pIdx, { label: e.target.value })} placeholder="Nombre de la fase" className="h-7 text-xs rounded-sm" />
                      <label className="flex items-center gap-1 text-[10px] text-zinc-600 whitespace-nowrap">
                        <Switch checked={p.requires_verification} onCheckedChange={(v) => updatePhase(tIdx, pIdx, { requires_verification: v })} />
                        Verif.
                      </label>
                      <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-zinc-400 hover:text-brand-danger" onClick={() => removePhase(tIdx, pIdx)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-zinc-500 whitespace-nowrap">Rol de la fase</span>
                      <select
                        className="border border-zinc-200 rounded-sm h-7 px-1 text-[11px] bg-white"
                        value={p.rol || ''}
                        onChange={(e) => updatePhase(tIdx, pIdx, { rol: e.target.value })}
                        data-testid={`fase-rol-${tIdx}-${pIdx}`}
                      >
                        <option value="">(sin rol)</option>
                        <option value="propietario">Quien tenga el caso</option>
                        {(teamRoles || []).map((r) => <option key={r.id} value={r.id}>{r.name || r.id}</option>)}
                      </select>
                      <span className="text-[10px] text-zinc-400">las subtareas lo heredan</span>
                    </div>

                    <div className="space-y-1">
                      {(p.default_subtasks || []).map((s, sIdx) => (
                        <div key={sIdx} className="flex items-center gap-1">
                          <Input
                            value={s.title}
                            onChange={(e) => updateSubtask(tIdx, pIdx, sIdx, { title: e.target.value })}
                            placeholder="Subtarea"
                            className="h-7 text-[11px] rounded-sm flex-1"
                          />
                          <select
                            className="border border-zinc-200 rounded-sm h-7 px-1 text-[11px] bg-white"
                            value={s.rol || ''}
                            onChange={(e) => updateSubtask(tIdx, pIdx, sIdx, { rol: e.target.value })}
                            data-testid={`subtarea-rol-${tIdx}-${pIdx}-${sIdx}`}
                          >
                            <option value="">(hereda la fase)</option>
                            <option value="propietario">Quien tenga el caso</option>
                            {(teamRoles || []).map((r) => <option key={r.id} value={r.id}>{r.name || r.id}</option>)}
                          </select>
                          {/* **El peso, que se preservaba y no se podía editar (abierto el
                              2026-10-05).** De acá sale cuántas horas nace con cada subtarea: el
                              esfuerzo del tipo de pedido × este porcentaje. El validador que avisa
                              cuando los pesos no suman 100 existía desde siempre y **avisaba sobre
                              datos que nadie podía escribir desde esta pantalla**. */}
                          <label
                            className="flex items-center gap-0.5 text-[10px] text-zinc-500"
                            title="Qué parte del esfuerzo del caso se lleva esta subtarea. Las horas con las que nace salen de multiplicar el esfuerzo típico del tipo de pedido por este porcentaje. Vacío = sin peso: la subtarea nace sin estimación. Los pesos de la plantilla tendrían que sumar 100."
                          >
                            <Input
                              type="number" min="0" max="100" step="5"
                              value={s.peso_pct ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                updateSubtask(tIdx, pIdx, sIdx, {
                                  // Cero **es** vacío: una subtarea que se lleva 0 % no es una con
                                  // peso cero, es una sin peso declarado.
                                  peso_pct: v === '' ? null : (Math.min(100, parseFloat(v)) || null),
                                });
                              }}
                              placeholder="—"
                              className="h-7 w-14 text-[11px] rounded-sm px-1"
                              data-testid={`subtarea-peso-${tIdx}-${pIdx}-${sIdx}`}
                            />
                            %
                          </label>
                          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-zinc-400 hover:text-brand-danger"
                            onClick={() => removeSubtask(tIdx, pIdx, sIdx)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                      <Button type="button" variant="outline" size="sm" className="h-6 text-[10px] rounded-sm"
                        onClick={() => addSubtask(tIdx, pIdx)}>
                        <Plus className="w-3 h-3 mr-1" /> Subtarea
                      </Button>
                    </div>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" className="h-7 text-[11px] rounded-sm" onClick={() => addPhase(tIdx)}>
                  <Plus className="w-3 h-3 mr-1" /> Agregar fase
                </Button>
              </div>

              <div className="flex justify-end">
                <Button type="button" variant="ghost" size="sm" className="h-7 text-[11px] text-brand-danger rounded-sm" onClick={() => removeTemplate(tIdx)}>
                  <Trash2 className="w-3 h-3 mr-1" /> Eliminar plantilla
                </Button>
              </div>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" className="h-8 text-xs rounded-sm" onClick={addTemplate}>
              <Plus className="w-3 h-3 mr-1" /> Agregar plantilla
            </Button>
            <Button type="button" size="sm" className="h-8 text-xs bg-brand-primary text-white rounded-sm" disabled={saving} onClick={saveTemplates}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
              Guardar plantillas
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-zinc-200">
        <CardHeader className="pb-2 border-b border-zinc-100">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2">
            <Bot className="w-4 h-4" /> Agente PM de area
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-3 text-xs">
          <label className="flex items-center gap-2">
            <Switch checked={!!pm.enabled} onCheckedChange={(v) => setPm((s) => ({ ...s, enabled: v }))} />
            <span className="font-medium">Activar agente PM</span>
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div>
              <Label className="text-[10px] text-zinc-500">Hora standup</Label>
              <Input type="number" min="0" max="23" value={pm.standup_hour} onChange={(e) => setPm((s) => ({ ...s, standup_hour: e.target.value }))} className="h-8 text-xs rounded-sm mt-0.5" />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">Hora seguimiento</Label>
              <Input type="number" min="0" max="23" value={pm.followup_hour} onChange={(e) => setPm((s) => ({ ...s, followup_hour: e.target.value }))} className="h-8 text-xs rounded-sm mt-0.5" />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">UTC offset</Label>
              <Input type="number" min="-12" max="14" value={pm.utc_offset_hours} onChange={(e) => setPm((s) => ({ ...s, utc_offset_hours: e.target.value }))} className="h-8 text-xs rounded-sm mt-0.5" />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">Canal</Label>
              <select value={pm.channel} onChange={(e) => setPm((s) => ({ ...s, channel: e.target.value }))} className="w-full border border-brand-border rounded-sm h-8 px-2 text-xs mt-0.5 bg-white">
                <option value="whatsapp">whatsapp</option>
                <option value="teams">teams (futuro)</option>
                <option value="email">email (futuro)</option>
              </select>
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2"><Switch checked={!!pm.remind_due} onCheckedChange={(v) => setPm((s) => ({ ...s, remind_due: v }))} /> Vencimientos</label>
            <label className="flex items-center gap-2"><Switch checked={!!pm.remind_effort} onCheckedChange={(v) => setPm((s) => ({ ...s, remind_effort: v }))} /> Carga de esfuerzo</label>
            <label className="flex items-center gap-2"><Switch checked={!!pm.remind_tests} onCheckedChange={(v) => setPm((s) => ({ ...s, remind_tests: v }))} /> Tests/verificacion</label>
          </div>
          <Button type="button" size="sm" className="h-8 text-xs bg-brand-primary text-white rounded-sm" disabled={saving} onClick={savePm}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
            Guardar politica PM
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
